import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProductEvaluationForm.module.css';
import {
  TextField,
  CircularProgress,
  Typography,
  Box,
  Paper,
  Divider
} from '@mui/material';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { DataProviderInstance } from '../../../api/DataProvider';
import { FeedbackConfirmation, Toast } from '../../../components/support';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import CustomButton from '../../../components/buttons/CustomButton';
import ColorPalette from '../../../colorpalette/ColorPalette';

function ProductEvaluationForm() {
  const navigate = useNavigate();
  const { EvaluateProduct, GetUserNps } = DataProviderInstance();
  
  const [formData, setFormData] = useState({
    rating: null,
    comments: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasExistingEvaluation, setHasExistingEvaluation] = useState(false);
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(window.location.pathname);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load the user's most recent NPS evaluation when the component mounts
  useEffect(() => {
    setIsLoading(true);
    
    // Fetch the user's most recent NPS evaluation
    GetUserNps((response) => {
      setIsLoading(false);
      
      if (response.success && response.nps_data) {
        // Update the form with the most recent evaluation data
        setFormData({
          rating: parseInt(response.nps_data.rating),
          comments: response.nps_data.comments || ''
        });
        setHasExistingEvaluation(true);
      }
    });
  }, [GetUserNps]);

  const handleRatingSelect = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
    
    // Clear error when rating is selected
    if (errors.rating) {
      setErrors(prev => ({
        ...prev,
        rating: ''
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getRatingCategory = (rating) => {
    if (rating >= 9) return 'promoter';
    if (rating >= 7) return 'neutral';
    return 'detractor';
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.rating === null) {
      newErrors.rating = 'Por favor, selecione uma nota de 0 a 10';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData object for submission
      const submitData = new FormData();
      submitData.append('rating', formData.rating);
      submitData.append('comments', formData.comments);
      submitData.append('currentRoute', currentRoute);
      
      // Call API to submit the NPS evaluation
      await EvaluateProduct(submitData, (response) => {
        if (response.success) {
          setSubmitSuccess(true);
          // Show success toast
          setToast({
            open: true,
            message: 'Avaliação enviada com sucesso!',
            severity: 'success'
          });
        } else {
          // Show error toast
          setToast({
            open: true,
            message: response.error || 'Ocorreu um erro ao enviar o formulário. Tente novamente.',
            severity: 'error'
          });
          setErrors({ submit: response.error || 'Ocorreu um erro ao enviar o formulário. Tente novamente.' });
        }
        setIsSubmitting(false);
      });
    } catch (error) {
      // Show error toast
      setToast({
        open: true,
        message: 'Ocorreu um erro ao enviar o formulário. Tente novamente.',
        severity: 'error'
      });
      setErrors({ submit: 'Ocorreu um erro ao enviar o formulário. Tente novamente.' });
      setIsSubmitting(false);
    }
  };

  // Handle toast close
  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast({ ...toast, open: false });
  };

  if (submitSuccess && formData.rating >= 9) {
    return (
      <FeedbackConfirmation
        title="Que bom que a Gabriela ajuda você!"
        message="Obrigado pela avaliação. Se conhece alguém que também ganharia tempo com a Gabriela, envie o seu link em Indique e ganhe: quando a pessoa assina, você ganha um mês do seu plano."
        buttonText="Indicar alguém"
        returnPath="/indique"
      />
    );
  }

  if (submitSuccess) {
    return (
      <FeedbackConfirmation
        title="Avaliação enviada com sucesso!"
        message="Agradecemos por avaliar nosso produto. Sua opinião é muito importante para continuarmos melhorando."
        buttonText="Voltar para o Dashboard"
        returnPath="/dashboard"
      />
    );
  }

  return (
    <div className='page-content'>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: 'var(--shadow-md)'
        }}
      >
        <AreaTitle
          Title="Avaliar Produto (NPS)"
          Icon={<RateReviewOutlinedIcon />}
          Description="Em uma escala de 0 a 10, qual a probabilidade de você recomendar a Gabriela para um amigo ou colega? Sua avaliação nos ajuda a melhorar continuamente nosso produto."
        />
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : hasExistingEvaluation ? (
          <Box sx={{ mt: 2, mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <em>Sua avaliação anterior foi carregada. Você pode modificá-la e enviar novamente se desejar.</em>
            </Typography>
          </Box>
        ) : null}
        
        <Divider sx={{ marginTop: '16px', marginBottom: '24px' }} />
        
        <form onSubmit={handleSubmit}>
          <div className={styles.ratingContainer} style={{ marginBottom: '32px' }}>
            <Typography className={styles.ratingLabel} style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
              <StarOutlinedIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Sua avaliação
            </Typography>
            
            <div className={styles.ratingSelector}>
              {[...Array(11)].map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`${styles.ratingButton} ${
                    formData.rating === index ? styles.selected : ''
                  } ${formData.rating === index ? styles[getRatingCategory(index)] : ''}`}
                  onClick={() => handleRatingSelect(index)}
                >
                  {index}
                </button>
              ))}
            </div>
            
            <div className={styles.ratingLabels} style={{ marginTop: '8px' }}>
              <span>Pouco provável</span>
              <span>Muito provável</span>
            </div>
            
            {errors.rating && (
              <Typography className={styles.errorMessage} color="error" style={{ marginTop: '8px' }}>
                {errors.rating}
              </Typography>
            )}
          </div>

          <div className={styles.formGroup} style={{ marginBottom: '32px' }}>
            <TextField
              label="Comentários (opcional)"
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              placeholder="Conte-nos mais sobre sua experiência com a Gabriela"
            />
          </div>

          {errors.submit && (
            <Box mt={2} mb={2}>
              <Typography color="error">{errors.submit}</Typography>
            </Box>
          )}

          {/* Toast notification */}
          <Toast
            open={toast.open}
            onClose={handleToastClose}
            message={toast.message}
            severity={toast.severity}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <CustomButton
              type="submit"
              variant="contained"
              style="primary"
              label={isSubmitting ? <CircularProgress size={24} /> : hasExistingEvaluation ? 'Atualizar Avaliação' : 'Enviar Avaliação'}
              disabled={isSubmitting}
              className={styles.submitButton}
            />
          </div>
        </form>
      </Paper>
    </div>
  );
}

export default ProductEvaluationForm;